import { ErrorInfo, ReactNode } from 'react';
import { ErrorBoundary as BaseErrorBoundary, ErrorBoundaryProps } from 'react-error-boundary';

import { T } from '@oaknorth/util-intl';
import { getLogger } from '@oaknorth/util-rum';

import { Button } from '../../chakra';
import { Status } from '../../types';

import { Result } from './Result';

export function ErrorBoundary({
  children,
  onError,
  ...props
}: {
  children: ReactNode;
  /**
   * Callback on error
   *
   * If it returns true, it will be treated as the error having been handled
   */
  onError?: (error: Error, info: ErrorInfo) => boolean | void;
} & Pick<ErrorBoundaryProps, 'resetKeys' | 'fallbackRender'>) {
  const onErrorCallback = (error: Error, info: ErrorInfo) => {
    if (onError) {
      const handled = onError(error, info);
      if (handled) {
        return;
      }
    }
    const logger = getLogger();
    const newError = new Error(error.message);

    // eslint-disable-next-line @oaknorth/ui/no-template-literals -- not visible to user
    newError.name = `React ErrorBoundary ${error.name}`;
    newError.stack = info.componentStack ?? undefined;
    logger.exception(newError);
  };

  return (
    <BaseErrorBoundary
      fallbackRender={({ resetErrorBoundary }) => (
        <Result
          status={Status.ERROR}
          title={T`errorBoundary.title`}
          text={T`errorBoundary.description`}
          actions={<Button variant="solid" onClick={resetErrorBoundary}>{T`errorBoundary.resetButton.text`}</Button>}
        />
      )}
      onError={onErrorCallback}
      {...props}
    >
      {children}
    </BaseErrorBoundary>
  );
}
